import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
    TextField, Typography,
} from '@mui/material';
import { EMPLOYEE_POSITION } from 'constants';
import { positionChangeValidationSchema } from 'utils/validationSchemas';

export const PositionChangeModal = ({ mode, currentPosition, saving, error, disabled, onClose, onSubmit }) => {
    const titleId = useId();
    const direct = mode === 'direct';
    const availablePositions = Object.values(EMPLOYEE_POSITION);
    const defaultPosition = availablePositions.includes(currentPosition)
        ? currentPosition
        : '';
    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: { position: defaultPosition },
        resolver: yupResolver(positionChangeValidationSchema),
        context: { currentPosition },
    });

    return (
        <Dialog open fullWidth maxWidth="xs" aria-labelledby={titleId}
            onClose={saving ? undefined : onClose}>
            <DialogTitle id={titleId}>{direct ? 'Edit position' : 'Request position change'}</DialogTitle>
            <form onSubmit={handleSubmit(({ position }) => onSubmit(position))}>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography>Current position: {currentPosition || 'Not set'}</Typography>
                    {!direct && <Typography variant="body2">
                        Your current position stays unchanged until an administrator approves your request.
                    </Typography>}
                    {error && <Alert severity="error">{error}</Alert>}
                    <Controller name="position" control={control} render={({ field }) => (
                        <TextField {...field} select required autoFocus fullWidth label="New position"
                            disabled={saving} error={!!errors.position}
                            helperText={errors.position?.message}>
                            {availablePositions.map((position) => (
                                <MenuItem key={position} value={position}>{position}</MenuItem>
                            ))}
                        </TextField>
                    )} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>Close</Button>
                    <Button type="submit" variant="contained" disabled={saving || disabled}>
                        {saving ? 'Saving…' : direct ? 'Save' : 'Submit request'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

PositionChangeModal.propTypes = {
    mode: PropTypes.oneOf(['request', 'direct']).isRequired,
    currentPosition: PropTypes.string,
    saving: PropTypes.bool.isRequired,
    error: PropTypes.string,
    disabled: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};
